import { useLayoutEffect, useRef, type RefObject } from "react";
import { EASE_OUT } from "./motion";

export type TileBox = {
  left: number;
  top: number;
  width: number;
  height: number;
};

const FILL_MS = 280;
const SPLIT_MS = 260;
const RADIUS = 8;

type Size = { width: number; height: number };

// A rounded clip that shows only `box` of a container-sized element, so a tile
// can grow to fill the container without its corners or label stretching.
function clipTo(box: TileBox, size: Size): string {
  const right = size.width - box.left - box.width;
  const bottom = size.height - box.top - box.height;
  return `inset(${box.top}px ${right}px ${bottom}px ${box.left}px round 8px)`;
}

// The outer edges of a level’s tiles, inside the gap around the container.
function boundsOf(boxes: Iterable<TileBox>): TileBox {
  let left = Infinity;
  let top = Infinity;
  let right = -Infinity;
  let bottom = -Infinity;
  for (const box of boxes) {
    left = Math.min(left, box.left);
    top = Math.min(top, box.top);
    right = Math.max(right, box.left + box.width);
    bottom = Math.max(bottom, box.top + box.height);
  }
  return { left, top, width: right - left, height: bottom - top };
}

const offset = ({ left, top }: TileBox) => `translate(${left}px, ${top}px)`;

// A copy of a tile that may already have left the DOM, so it can finish its
// part of the transition while the next level takes over.
function ghostOf(tile: HTMLElement): HTMLElement {
  const ghost = tile.cloneNode(true) as HTMLElement;
  ghost.removeAttribute("data-treemap-tile");
  ghost.removeAttribute("data-highlighted");
  ghost.removeAttribute("href");
  ghost.style.pointerEvents = "none";
  return ghost;
}

// Turns a ghost into a container-sized tile whose clip and label can then
// travel between a tile and the area a level fills.
function fillable(ghost: HTMLElement, size: Size): HTMLElement | null {
  Object.assign(ghost.style, {
    left: "0px",
    top: "0px",
    width: `${size.width}px`,
    height: `${size.height}px`,
  });
  return ghost.querySelector<HTMLElement>("[data-tile-text]");
}

function fade(el: HTMLElement, from: number, to: number, delay: number) {
  return el.animate([{ opacity: from }, { opacity: to }], {
    duration: SPLIT_MS,
    delay,
    easing: EASE_OUT,
    fill: "both",
  });
}

const near = (a: number, b: number) => Math.abs(a - b) < 0.5;

// One tile’s part of a level drawn as a single surface: grown across the gaps
// to its neighbours, rounded only where it meets the surface’s own corners.
function together(box: TileBox, area: TileBox, gap: number, color: string) {
  const outer = {
    left: near(box.left, area.left),
    top: near(box.top, area.top),
    right: near(box.left + box.width, area.left + area.width),
    bottom: near(box.top + box.height, area.top + area.height),
  };
  const left = box.left - (outer.left ? 0 : gap / 2);
  const top = box.top - (outer.top ? 0 : gap / 2);
  const width = box.left + box.width + (outer.right ? 0 : gap / 2) - left;
  const height = box.top + box.height + (outer.bottom ? 0 : gap / 2) - top;
  const corner = (a: boolean, b: boolean) => (a && b ? `${RADIUS}px` : "0px");
  return {
    transform: `translate(${left - box.left}px, ${top - box.top}px) scale(${width / box.width}, ${height / box.height})`,
    backgroundColor: color,
    borderRadius: [
      corner(outer.top, outer.left),
      corner(outer.top, outer.right),
      corner(outer.bottom, outer.right),
      corner(outer.bottom, outer.left),
    ].join(" "),
  };
}

// Parts a level from one surface in its parent’s color into its own tiles,
// opening the gaps between them, or closes them back into that surface.
function crack(
  el: HTMLElement,
  box: TileBox,
  area: TileBox,
  gap: number,
  parentColor: string,
  { closing, delay }: { closing: boolean; delay: number }
) {
  const joined = together(box, area, gap, parentColor);
  const apart = {
    transform: "none",
    backgroundColor: getComputedStyle(el).backgroundColor,
    borderRadius: `${RADIUS}px`,
  };
  el.style.transformOrigin = "0 0";
  const label = el.querySelector<HTMLElement>("[data-tile-text]");
  label?.animate(
    closing
      ? [{ opacity: 1 }, { opacity: 0, offset: 0.4 }, { opacity: 0 }]
      : [{ opacity: 0 }, { opacity: 0, offset: 0.4 }, { opacity: 1 }],
    { duration: SPLIT_MS, delay, fill: closing ? "forwards" : "backwards" }
  );
  return el.animate(closing ? [apart, joined] : [joined, apart], {
    duration: SPLIT_MS,
    delay,
    easing: EASE_OUT,
    fill: closing ? "forwards" : "backwards",
  });
}

// Drilling into a tile grows it to fill the treemap and then cracks it open into
// the next level; going back up closes the level into one tile and shrinks it
// into its place among its siblings.
export function useTreemapZoom(
  containerRef: RefObject<HTMLDivElement | null>,
  path: readonly string[] | undefined,
  boxes: ReadonlyMap<string, TileBox>,
  size: Size,
  gap: number
) {
  const previousRef = useRef<{
    path: string[];
    boxes: ReadonlyMap<string, TileBox>;
    tiles: Map<string, HTMLElement>;
  } | null>(null);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!path || !container || boxes.size === 0) return;
    const tiles = new Map<string, HTMLElement>();
    for (const el of container.querySelectorAll<HTMLElement>(
      ":scope > [data-treemap-tile]"
    )) {
      tiles.set(el.dataset.treemapTile!, el);
    }
    const previous = previousRef.current;
    previousRef.current = { path: [...path], boxes, tiles };
    if (
      !previous ||
      previous.path.join("/") === path.join("/") ||
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      return;
    }

    // Hover rings would draw seams across a level while it is one surface.
    container.dataset.zooming = "true";
    queueMicrotask(() =>
      Promise.all(
        container.getAnimations({ subtree: true }).map((a) => a.finished)
      ).then(() => delete container.dataset.zooming)
    );
    const isPrefix = (a: readonly string[], b: readonly string[]) =>
      a.length < b.length && a.every((key, i) => b[i] === key);

    const intoKey = isPrefix(previous.path, path)
      ? path[previous.path.length]!
      : null;
    const anchorIn = intoKey ? previous.boxes.get(intoKey) : undefined;
    const clickedTile = intoKey ? previous.tiles.get(intoKey) : undefined;
    if (anchorIn && clickedTile) {
      const siblings = [...previous.tiles]
        .filter(([key]) => key !== intoKey)
        .map(([, el]) => ghostOf(el));
      const filled = boundsOf(boxes.values());
      const filling = ghostOf(clickedTile);
      const label = fillable(filling, size);
      container.prepend(...siblings, filling);
      for (const ghost of siblings) {
        ghost.animate([{ opacity: 1 }, { opacity: 0 }], {
          duration: FILL_MS,
          easing: EASE_OUT,
          fill: "forwards",
        }).onfinish = () => ghost.remove();
      }
      filling.animate(
        [
          { clipPath: clipTo(anchorIn, size) },
          { clipPath: clipTo(filled, size) },
        ],
        { duration: FILL_MS, easing: EASE_OUT, fill: "forwards" }
      );
      label?.animate(
        [{ transform: offset(anchorIn) }, { transform: offset(filled) }],
        { duration: FILL_MS, easing: EASE_OUT, fill: "forwards" }
      );
      label?.animate([{ opacity: 1 }, { opacity: 0 }], {
        duration: FILL_MS / 2,
        delay: FILL_MS / 2,
        fill: "forwards",
      });
      const parentColor = getComputedStyle(filling).backgroundColor;
      for (const [key, el] of tiles) {
        crack(el, boxes.get(key)!, filled, gap, parentColor, {
          closing: false,
          delay: FILL_MS,
        });
        // Hidden while the clicked tile fills, then shown already joined over it.
        el.animate([{ opacity: 0 }, { opacity: 0 }], {
          duration: FILL_MS,
        }).onfinish = () => filling.remove();
      }
      return;
    }

    const outOfKey = isPrefix(path, previous.path)
      ? previous.path[path.length]!
      : null;
    const anchorOut = outOfKey ? boxes.get(outOfKey) : undefined;
    const parentTile = outOfKey ? tiles.get(outOfKey) : undefined;
    if (anchorOut && parentTile) {
      const children = [...previous.tiles].map(
        ([key, el]) => [ghostOf(el), previous.boxes.get(key)!] as const
      );
      const merged = boundsOf(previous.boxes.values());
      const merging = ghostOf(parentTile);
      const label = fillable(merging, size);
      container.append(merging, ...children.map(([ghost]) => ghost));
      const parentColor = getComputedStyle(parentTile).backgroundColor;
      for (const [ghost, box] of children) {
        crack(ghost, box, merged, gap, parentColor, {
          closing: true,
          delay: 0,
        }).onfinish = () => ghost.remove();
      }
      merging.animate(
        [
          { clipPath: clipTo(merged, size) },
          { clipPath: clipTo(anchorOut, size) },
        ],
        {
          duration: FILL_MS,
          delay: SPLIT_MS,
          easing: EASE_OUT,
          fill: "both",
        }
      ).onfinish = () => merging.remove();
      label?.animate(
        [{ transform: offset(merged) }, { transform: offset(anchorOut) }],
        {
          duration: FILL_MS,
          delay: SPLIT_MS,
          easing: EASE_OUT,
          fill: "both",
        }
      );
      parentTile.animate([{ opacity: 0 }, { opacity: 1 }], {
        duration: 1,
        // Shown a frame early, under the ghost that lands exactly on it.
        delay: SPLIT_MS + FILL_MS - 32,
        fill: "backwards",
      });
      for (const [key, el] of tiles) {
        if (key !== outOfKey) fade(el, 0, 1, SPLIT_MS);
      }
      return;
    }

    for (const el of tiles.values()) fade(el, 0, 1, 0);
  });
}
